import React, { useEffect, useState } from 'react';
import { saRequest } from '../../../utils/superAdminAxiosCaller';
import { describeAction } from './describeAction';

interface AuditDetail {
    id: number;
    action: string;
    super_admin_id: number | null;
    actor_email_snapshot: string | null;
    actor_name: string | null;
    target_type: string | null;
    target_id: number | null;
    target_company_id: number | null;
    target_company_name: string | null;
    target_user_email?: string | null;
    target_user_username?: string | null;
    target_user_fullname?: string | null;
    before_json: Record<string, any> | null;
    after_json: Record<string, any> | null;
    metadata_json: Record<string, any> | null;
    impersonation_session_id: number | null;
    ip: string | null;
    user_agent: string | null;
    occurred_at: string | null;
}

// Human labels for metadata keys we know about. Unknown keys fall back to
// the raw key name so new backend data still surfaces without UI updates.
const METADATA_LABELS: Record<string, string> = {
    target_user_id: 'Target user ID',
    target_user_email: 'Target user email',
    target_user_username: 'Target user username',
    target_user_fullname: 'Target user',
    target_company_id: 'Target company ID',
    target_company_name: 'Target company',
    duration_minutes: 'Duration',
    duration_seconds: 'Actual duration',
    reason: 'Reason',
    from_status: 'Previous status',
    changed_fields: 'Changed fields',
    expires_at: 'Expires at',
    request_count: 'Reads during session',
    write_attempt_count: 'Write attempts',
    method: 'HTTP method',
    route: 'Route',
    initial_admin: 'Initial admin',
    initial_admin_email: 'Initial admin email',
    initial_admin_user_id: 'Initial admin user ID',
    new_ip: 'New IP',
    attempted_email: 'Attempted email',
    recipients: 'Recipients',
    target_email: 'Target email',
    code_index: 'Recovery code index',
    filters: 'Filters',
    per_page: 'Per page',
    page: 'Page',
    row_count: 'Row count',
    total: 'Total rows',
    format: 'Export format',
    capped: 'Row cap hit',
    company_count: 'Companies',
    recipients_count: 'Recipients',
    overdue_ids: 'Overdue company IDs',
    imminent_ids: 'Imminent company IDs',
    upcoming_ids: 'Upcoming company IDs',
    revoked_tokens: 'Revoked tokens',
};

// Human-friendly formatter by key — ISO dates become locale strings, seconds
// become "2m 34s", etc. Returns null to say "use default formatting".
function formatMetaValue(key: string, value: any): string | null {
    if (value === null || value === undefined) return '—';
    if (key === 'duration_minutes' && typeof value === 'number') {
        return `${value} min`;
    }
    if (key === 'duration_seconds' && typeof value === 'number') {
        const m = Math.floor(value / 60);
        const s = value % 60;
        return m > 0 ? `${m}m ${s}s` : `${s}s`;
    }
    if (key === 'expires_at' && typeof value === 'string') {
        const d = new Date(value);
        if (!isNaN(d.getTime())) return d.toLocaleString();
    }
    if (key === 'reason' && typeof value === 'string') {
        // impersonation end reasons are terse codes; expand them.
        const map: Record<string, string> = {
            ttl: 'TTL expired (auto)',
            user: 'User pressed END SESSION',
            force: 'Force-ended by peer',
            write_blocked: 'Ended after blocked write',
        };
        return map[value] ?? value;
    }
    return null;
}

interface Props {
    id: number;
    onClose: () => void;
}

/**
 * Read-only detail view for a single audit row. The meat is the before/after
 * diff — rendered as JSON blocks with the changed keys highlighted. Best-
 * effort: the log is a snapshot, and some entries have no before/after (e.g.
 * pure events like login, logout).
 */
const AuditLogDetailModal: React.FC<Props> = ({ id, onClose }) => {
    const [row, setRow] = useState<AuditDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    useEffect(() => {
        setLoading(true);
        saRequest<{ data: AuditDetail }>('GET', `audit-log/${id}`)
            .then(res => setRow(res.data.data))
            .catch(err => setError(err?.response?.data?.message || 'Failed to load row.'))
            .finally(() => setLoading(false));
    }, [id]);

    // Keys that changed between before and after — drives highlighting in
    // both columns. We compare a shallow JSON-stringified value so nested
    // objects still register as "changed" without a full deep diff.
    const changedKeys = React.useMemo(() => {
        if (!row || (row.before_json === null && row.after_json === null)) return new Set<string>();
        const before = row.before_json ?? {};
        const after = row.after_json ?? {};
        const keys = new Set<string>([...Object.keys(before), ...Object.keys(after)]);
        const changed = new Set<string>();
        keys.forEach(k => {
            if (JSON.stringify((before as any)[k]) !== JSON.stringify((after as any)[k])) {
                changed.add(k);
            }
        });
        return changed;
    }, [row]);

    return (
        <div className="sa-modal-backdrop" onClick={onClose}>
            <div
                className="sa-modal"
                onClick={e => e.stopPropagation()}
                style={{ maxWidth: 900, maxHeight: '85vh', overflow: 'auto' }}
            >
                {loading ? (
                    <div style={{ color: 'var(--sa-text-muted)', fontSize: 13 }}>Loading…</div>
                ) : error ? (
                    <div className="sa-error-banner">{error}</div>
                ) : row ? (
                    <>
                        <h2 style={{ margin: 0, fontSize: 'var(--sa-fs-body)', fontWeight: 600, lineHeight: 1.4 }}>
                            {describeAction({
                                action: row.action,
                                actor_email: row.actor_email_snapshot,
                                actor_name: row.actor_name,
                                target_type: row.target_type,
                                target_id: row.target_id,
                                target_company_id: row.target_company_id,
                                target_company_name: row.target_company_name,
                                target_user_email: row.target_user_email ?? null,
                                target_user_username: row.target_user_username ?? null,
                                target_user_fullname: row.target_user_fullname ?? null,
                                metadata: row.metadata_json,
                            })}
                        </h2>
                        <div style={{ color: 'var(--sa-text-tertiary)', fontSize: 'var(--sa-fs-xs)', marginTop: 6 }}>
                            <span className="sa-mono">{row.action}</span>
                            {' · '}Row #{row.id}
                            {' · '}{row.occurred_at && new Date(row.occurred_at).toLocaleString()}
                        </div>

                        <hr className="sa-hairline" style={{ margin: 'var(--sa-s4) 0' }} />

                        <dl className="sa-dl">
                            <dt>Actor</dt>
                            <dd>
                                {row.actor_email_snapshot ? (
                                    <>
                                        <span className="sa-mono">{row.actor_email_snapshot}</span>
                                        {row.actor_name && <span style={{ color: 'var(--sa-text-tertiary)' }}> · {row.actor_name}</span>}
                                        {row.super_admin_id !== null && <span style={{ color: 'var(--sa-text-tertiary)' }}> · #{row.super_admin_id}</span>}
                                    </>
                                ) : (
                                    <span style={{ fontStyle: 'italic', color: 'var(--sa-text-tertiary)' }}>system</span>
                                )}
                            </dd>

                            {row.target_type && (
                                <>
                                    <dt>Target</dt>
                                    <dd>
                                        <span className="sa-mono">{row.target_type}</span>
                                        {row.target_id !== null && <span style={{ color: 'var(--sa-text-tertiary)' }}> #{row.target_id}</span>}
                                        {row.target_company_name && (
                                            <span style={{ color: 'var(--sa-text-tertiary)' }}> · company: {row.target_company_name}</span>
                                        )}
                                    </dd>
                                </>
                            )}

                            {row.impersonation_session_id !== null && (
                                <>
                                    <dt>Impersonation</dt>
                                    <dd>
                                        <span className="sa-mono" style={{ color: 'var(--sa-accent)' }}>
                                            session #{row.impersonation_session_id}
                                        </span>
                                    </dd>
                                </>
                            )}

                            <dt>IP</dt>
                            <dd className="sa-mono">{row.ip ?? '—'}</dd>

                            {row.user_agent && (
                                <>
                                    <dt>User agent</dt>
                                    <dd className="sa-mono" style={{ fontSize: 11, color: 'var(--sa-text-tertiary)' }}>
                                        {row.user_agent}
                                    </dd>
                                </>
                            )}
                        </dl>

                        {row.metadata_json !== null && Object.keys(row.metadata_json).length > 0 && (
                            <>
                                <div className="sa-section-label" style={{ margin: 'var(--sa-s4) 0 var(--sa-s2)' }}>
                                    Details
                                </div>
                                <MetadataTable value={row.metadata_json} />
                            </>
                        )}

                        {(row.before_json !== null || row.after_json !== null) && (
                            <>
                                <div className="sa-section-label" style={{ margin: 'var(--sa-s4) 0 var(--sa-s2)' }}>
                                    Before / after
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sa-s3)' }}>
                                    <div>
                                        <div style={{ fontSize: 'var(--sa-fs-xs)', color: 'var(--sa-text-tertiary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                            Before
                                        </div>
                                        <JsonBlock value={row.before_json} highlightKeys={changedKeys} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 'var(--sa-fs-xs)', color: 'var(--sa-text-tertiary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                            After
                                        </div>
                                        <JsonBlock value={row.after_json} highlightKeys={changedKeys} />
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="sa-modal-actions">
                            <button type="button" className="sa-btn sa-btn-secondary" onClick={onClose}>
                                Close
                            </button>
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    );
};

function JsonBlock({ value, highlightKeys }: { value: Record<string, any> | null; highlightKeys?: Set<string> }) {
    if (value === null || typeof value !== 'object') {
        return (
            <pre style={{ fontSize: 11, color: 'var(--sa-text-tertiary)', padding: 'var(--sa-s3)', background: 'var(--sa-shell-bg)', borderRadius: 'var(--sa-radius)', border: '1px solid var(--sa-border)', margin: 0 }}>
                (empty)
            </pre>
        );
    }

    return (
        <pre style={{
            fontFamily: 'var(--sa-font-mono)',
            fontSize: 11,
            padding: 'var(--sa-s3)',
            background: 'var(--sa-shell-bg)',
            borderRadius: 'var(--sa-radius)',
            border: '1px solid var(--sa-border)',
            margin: 0,
            overflow: 'auto',
            maxHeight: 280,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
        }}>
            {Object.keys(value).map(k => {
                const isChanged = highlightKeys?.has(k);
                return (
                    <div
                        key={k}
                        style={{
                            background: isChanged ? 'rgba(245, 158, 11, 0.12)' : 'transparent',
                            padding: isChanged ? '2px 4px' : 0,
                            borderRadius: 2,
                            marginBottom: 2,
                        }}
                    >
                        <span style={{ color: isChanged ? 'var(--sa-accent)' : 'var(--sa-text-secondary)' }}>
                            {k}
                        </span>
                        <span style={{ color: 'var(--sa-text-tertiary)' }}>: </span>
                        <span style={{ color: 'var(--sa-text)' }}>
                            {JSON.stringify(value[k], null, 2)}
                        </span>
                    </div>
                );
            })}
        </pre>
    );
}

function MetadataTable({ value }: { value: Record<string, any> }) {
    const keys = Object.keys(value);
    if (keys.length === 0) return null;

    return (
        <dl className="sa-dl">
            {keys.map(k => {
                const raw = value[k];
                const label = METADATA_LABELS[k] ?? k;
                const formatted = formatMetaValue(k, raw);

                return (
                    <React.Fragment key={k}>
                        <dt>{label}</dt>
                        <dd>
                            {formatted !== null ? (
                                <span>{formatted}</span>
                            ) : raw === null || raw === undefined ? (
                                <span style={{ color: 'var(--sa-text-tertiary)' }}>—</span>
                            ) : typeof raw === 'boolean' ? (
                                <span className="sa-mono">{raw ? 'true' : 'false'}</span>
                            ) : typeof raw === 'number' ? (
                                <span className="sa-mono">{raw.toLocaleString()}</span>
                            ) : typeof raw === 'string' ? (
                                <span>{raw}</span>
                            ) : Array.isArray(raw) ? (
                                raw.length === 0 ? (
                                    <span style={{ color: 'var(--sa-text-tertiary)' }}>(empty)</span>
                                ) : (
                                    <span className="sa-mono" style={{ fontSize: 'var(--sa-fs-xs)' }}>
                                        [{raw.map(v => typeof v === 'string' ? `"${v}"` : String(v)).join(', ')}]
                                    </span>
                                )
                            ) : (
                                // Nested object — fall back to pretty JSON so we don't lose data.
                                <pre
                                    style={{
                                        fontFamily: 'var(--sa-font-mono)',
                                        fontSize: 11,
                                        padding: 'var(--sa-s2) var(--sa-s3)',
                                        background: 'var(--sa-shell-bg)',
                                        border: '1px solid var(--sa-border)',
                                        borderRadius: 'var(--sa-radius-sm)',
                                        margin: 0,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    {JSON.stringify(raw, null, 2)}
                                </pre>
                            )}
                        </dd>
                    </React.Fragment>
                );
            })}
        </dl>
    );
}

export default AuditLogDetailModal;
